import { useMemo } from 'react';
import { MediaWidget } from '@/components/events/detail/tabs/common/media/media.widget.tsx';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  buildPlaylistMediaRows,
  getFlowPlaylist,
  getFlowPlaylistMedia,
  playlistAllowsContributorAddMedia,
  playlistAllowsContributorReorder,
} from '@/helpers/flow-media-playlist.helper';
import type { FlowPlaylistSource } from '@/helpers/flow-media-playlist.helper';
import { useTranslations } from '@/i18n/use-translations';
import {
  flowPlaylistScopeAllowsHostRequest,
  type FlowPlaylistScope,
  type NonSlotFlowPlaylistScope,
} from '@/types/dtos/media/flow-playlist-scope.enum';
import { CONTRIBUTOR_FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/media-settings.dto';
import PlaylistMediaSettingsHeaderActions from '@/components/media/playlist-media-settings-switches';
import type { MediaListDisplayVariant } from '../flow-detail.types';

const PLAYLIST_TITLE_KEYS = {
  pre_flow: 'flows.detail.playlistMedia.preFlow',
  flow_intro: 'flows.detail.playlistMedia.flowIntro',
  flow_transition: 'flows.detail.playlistMedia.flowTransition',
  flow_resume: 'flows.detail.playlistMedia.flowResume',
  flow_outro: 'flows.detail.playlistMedia.flowOutro',
  after_flow: 'flows.detail.playlistMedia.afterFlow',
} as const satisfies Record<NonSlotFlowPlaylistScope, string>;

type FlowPlaylistMediaWidgetProps = {
  flow: FlowPlaylistSource;
  scope: FlowPlaylistScope;
  slotId?: string;
  slotTitle?: string;
  liveReady?: boolean;
  enabled?: boolean;
  disableBorder?: boolean;
  disableBackground?: boolean;
  disableHover?: boolean;
  disableShadow?: boolean;
  requestButtonLabel?: string;
  requestSubmitLabel?: string;
  disablePlayerQuery?: boolean;
  /** Hide create/reorder policy switches (e.g. contributor role details modal). */
  hideMediaSettingsSwitches?: boolean;
  displayVariant?: MediaListDisplayVariant;
  enablePlayerSocket?: boolean;
  scrollableList?: boolean;
};

export function FlowPlaylistMediaWidget({
  flow,
  scope,
  slotId,
  slotTitle,
  liveReady = false,
  enabled = false,
  disableBorder,
  disableBackground,
  disableHover,
  disableShadow,
  requestButtonLabel,
  requestSubmitLabel,
  disablePlayerQuery,
  hideMediaSettingsSwitches = false,
  displayVariant = 'default',
  enablePlayerSocket = false,
  scrollableList,
}: FlowPlaylistMediaWidgetProps) {
  const isFlowControl = displayVariant === 'flowControl';
  const t = useTranslations();
  const parentId = flow.origin ? getObjectId(flow.origin as Parameters<typeof getObjectId>[0]) : flow.id;
  const playlist = getFlowPlaylist(flow, scope, slotId);
  const media = getFlowPlaylistMedia(flow, scope, slotId);
  const playlistRows = buildPlaylistMediaRows(playlist, media);

  const widgetTitle = useMemo(() => {
    if (scope === 'slot') {
      return slotTitle
        ? t('flows.detail.playlistMedia.slotWithTitle', { slotTitle })
        : t('flows.detail.playlistMedia.break');
    }
    return t(PLAYLIST_TITLE_KEYS[scope]);
  }, [scope, slotTitle, t]);

  // Contributor dashboard only — not flow Control/Perform (those hide switches via isFlowControl).
  const isContributorFacing = hideMediaSettingsSwitches && !isFlowControl;
  const isContributorScope = CONTRIBUTOR_FLOW_PLAYLIST_SCOPES.includes(
    scope as (typeof CONTRIBUTOR_FLOW_PLAYLIST_SCOPES)[number],
  );
  const scopeCaps =
    isContributorScope && flow.mediaSettingsCapabilities?.scopes
      ? flow.mediaSettingsCapabilities.scopes[scope as (typeof CONTRIBUTOR_FLOW_PLAYLIST_SCOPES)[number]]
      : undefined;
  const scopeKey = scope as (typeof CONTRIBUTOR_FLOW_PLAYLIST_SCOPES)[number];
  const scopeStored = flow.mediaSettings?.hosts?.scopes?.[scopeKey];
  const storedScope = {
    allowSelfCreate: scopeStored?.allowSelfCreate ?? flow.mediaSettings?.hosts?.allowSelfCreate ?? true,
    allowReorder: scopeStored?.allowReorder ?? flow.mediaSettings?.hosts?.allowReorder ?? true,
  };
  const effectiveScope = flow.effectiveMediaSettings?.hosts?.scopes?.[scopeKey];
  const hostsEffective = flow.effectiveMediaSettings?.hosts;
  const effectiveCreate =
    effectiveScope?.allowSelfCreate ??
    hostsEffective?.allowSelfCreate ??
    (isContributorFacing ? false : storedScope.allowSelfCreate);
  const effectiveReorder =
    effectiveScope?.allowReorder ??
    hostsEffective?.allowReorder ??
    (isContributorFacing ? false : storedScope.allowReorder);
  const canAddMedia = playlistAllowsContributorAddMedia(
    effectiveCreate,
    playlist,
    isContributorFacing ? false : flow.mediaSettingsCapabilities?.canEdit,
  );
  const canReorderPlaylist = playlistAllowsContributorReorder(
    effectiveReorder,
    playlist,
    isContributorFacing ? false : flow.mediaSettingsCapabilities?.canEdit,
  );

  const settingsHeaderActions =
    !hideMediaSettingsSwitches && !isFlowControl && isContributorScope && effectiveScope && scopeCaps ? (
      <PlaylistMediaSettingsHeaderActions
        mode="flowScope"
        flowId={parentId}
        scope={scopeKey}
        stored={storedScope}
        effective={effectiveScope}
        canEdit={scopeCaps.canEdit}
      />
    ) : undefined;

  const showRequest =
    !isFlowControl && flowPlaylistScopeAllowsHostRequest(scope) && (isContributorFacing ? canAddMedia : true);

  const isBreakSlotPlaylist = scope === 'slot' && Boolean(slotId);

  return (
    <MediaWidget
      media={media}
      playlist={playlist}
      playlistRows={playlistRows}
      parentId={parentId}
      parentType="Flow"
      context={isBreakSlotPlaylist ? 'Break' : 'Flow'}
      entityId={isBreakSlotPlaylist ? slotId : undefined}
      contextParentId={parentId}
      contextParentType="Flow"
      placement={scope === 'slot' && slotId ? { id: slotId, type: 'Slot' } : undefined}
      playlistScope={scope}
      playlistSlotId={slotId}
      widgetTitle={widgetTitle}
      liveReady={liveReady}
      enablePlayerSocket={enablePlayerSocket}
      enabled={enabled}
      contributorCanAdd={isFlowControl ? false : canAddMedia}
      canReorderPlaylist={isFlowControl ? false : canReorderPlaylist}
      showRequestButton={showRequest}
      displayVariant={displayVariant}
      disableBorder={disableBorder}
      disableBackground={disableBackground}
      disableHover={disableHover}
      disableShadow={disableShadow}
      requestButtonLabel={requestButtonLabel}
      requestSubmitLabel={requestSubmitLabel}
      disablePlayerQuery={disablePlayerQuery}
      settingsHeaderActions={settingsHeaderActions}
      scrollableList={scrollableList}
      contributorFacing={isContributorFacing}
    />
  );
}
